export type ResultColumn = { key: string; label: string };

export type ResultTypeConfig = {
  category: string;
  subtype?: string;
  key: string;
  label: string;
  implemented: boolean;
  source?: string;
  materialScope?: "pavement" | "filter" | "subballast" | "soils";
  columns: ResultColumn[];
};

const baseColumns: ResultColumn[] = [
  { key: "folio", label: "Folio" },
  { key: "fecha_prueba", label: "Fecha" },
  { key: "frente", label: "Frente" },
  { key: "subfrente", label: "Subfrente" },
  { key: "ubicacion", label: "Ubicación" },
  { key: "dictamen", label: "Dictamen" },
];

function configured(category: string, subtype: string | undefined, key: string, source: string, extra: Partial<ResultTypeConfig> = {}): ResultTypeConfig {
  return { category, subtype, key, label: subtype ? `${category} / ${subtype}` : category, implemented: true, source, columns: baseColumns, ...extra };
}

function pending(category: string, subtype: string | undefined, key: string): ResultTypeConfig {
  return { category, subtype, key, label: subtype ? `${category} / ${subtype}` : category, implemented: false, columns: [] };
}

const petreosPavimentoSubbalastoColumns: ResultColumn[] = [
  { key: "folio", label: "Folio" },
  { key: "entregable", label: "Estudio" },
  { key: "fecha_prueba", label: "Fecha" },
  { key: "frente", label: "Frente" },
  { key: "subfrente", label: "Subfrente" },
  { key: "actividad_material", label: "Material" },
  { key: "procedencia_n_comun", label: "Procedencia" },
  { key: "entidad", label: "Entidad" },
  { key: "dictamen", label: "Dictamen" },
];

const petreosFiltroColumns: ResultColumn[] = [
  { key: "folio", label: "Folio" },
  { key: "fecha_prueba", label: "Fecha" },
  { key: "frente", label: "Frente" },
  { key: "subfrente", label: "Subfrente" },
  { key: "actividad_material", label: "Material" },
  { key: "procedencia_n_comun", label: "Procedencia" },
  { key: "entidad", label: "Entidad" },
  { key: "dictamen", label: "Dictamen" },
];

const aggregateAndSoilColumns: ResultColumn[] = [
  { key: "folio", label: "Folio" },
  { key: "fecha_prueba", label: "Fecha de muestreo" },
  { key: "lugar_muestreo", label: "Lugar" },
  { key: "frente", label: "Frente" },
  { key: "subfrente", label: "Subfrente" },
  { key: "procedencia_n_comun", label: "Procedencia" },
  { key: "entidad", label: "Entidad" },
  { key: "dictamen", label: "Dictamen" },
];

const soilColumns: ResultColumn[] = [
  { key: "folio", label: "Folio" },
  { key: "fecha_prueba", label: "Fecha de muestreo" },
  { key: "actividad_material", label: "Material" },
  { key: "procedencia_n_comun", label: "Procedencia" },
  { key: "frente", label: "Frente" },
  { key: "subfrente", label: "Subfrente" },
  { key: "entidad", label: "Entidad" },
  { key: "dictamen", label: "Dictamen" },
];

export const resultTypes: readonly ResultTypeConfig[] = [
  pending("Acero", "Estructural", "acero_estructural"),
  configured("Acero", "Refuerzo", "acero_refuerzo", "calidad.acero_refuerzo", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "estudio", label: "Estudio" },
    { key: "designacion", label: "No" }, { key: "grado", label: "Grado" }, { key: "proveedor", label: "Proveedor" }, { key: "colada_lote", label: "Colada/Lote" },
    { key: "entidad", label: "Entidad" }, { key: "dictamen", label: "Dictamen" },
  ] }),
  configured("Agregados", undefined, "agregados", "calidad.agregados", { columns: aggregateAndSoilColumns }),
  pending("Albañilería", "Cerámicos", "albanileria_ceramicos"),
  configured("Albañilería", "Mampostería", "mamposteria", "calidad.mamposteria", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" },
    { key: "material", label: "Material" }, { key: "tipo_pieza", label: "Tipo" }, { key: "lote", label: "Lote" }, { key: "dictamen", label: "Dictamen" },
  ] }),
  configured("Concreto", undefined, "concreto", "calidad.concreto", { columns: [
    { key: "folio", label: "Folio" }, { key: "muestra", label: "Id de la muestra" }, { key: "fecha_prueba", label: "Fecha de muestreo" },
    { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" }, { key: "elemento", label: "Elemento" },
    { key: "ubicacion", label: "Ubicación" }, { key: "porcentaje", label: "%" },
    { key: "resistencia_proyecto", label: "F'C Proy." }, { key: "entidad", label: "Entidad" },
    { key: "dictamen", label: "Dictamen" },
  ] }),
  configured("Compactaciones", "Suelos", "compactacion_suelos", "calidad.compactacion_suelos", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" },
    { key: "actividad_material", label: "Material" }, { key: "ubicacion_detalles", label: "Ubicación" }, { key: "capa", label: "Capa" },
    { key: "entidad", label: "Entidad" }, { key: "dictamen", label: "Dictamen" },
  ] }),
  pending("Compactaciones", "Mezclas asfálticas", "compactacion_asfaltica"),
  configured("Estructuras", "Conexiones atornilladas", "conexiones_atornilladas", "calidad.conexiones_atornilladas", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" },
    { key: "ubicacion_conexion", label: "Ubicación" }, { key: "tipo_conexion", label: "Tipo de conexión" },
    { key: "cantidad_tornillos_inspeccionados", label: "Inspeccionados" }, { key: "dictamen", label: "Dictamen" },
  ] }),
  pending("Estructuras", "Recubrimiento", "estructuras_recubrimiento"),
  configured("Estructuras", "Soldadura", "estructuras_soldadura", "calidad.pnd", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" },
    { key: "entregable", label: "Estudio" }, { key: "etapa", label: "Lugar" }, { key: "ubicacion_resultado", label: "Elemento/Ubicación" },
    { key: "juntas_total", label: "Juntas" }, { key: "juntas_no_cumple", label: "No cumplen" }, { key: "entidad", label: "Entidad" },
  ] }),
  configured("Pétreos", "Balasto", "petreos_balasto", "calidad.balasto", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" },
    { key: "t_nom", label: "Tamaño No" }, { key: "procedencia_n_comun", label: "Procedencia" }, { key: "entidad", label: "Entidad" }, { key: "dictamen", label: "Dictamen" },
  ] }),
  configured("Pétreos", "Pavimento", "petreos_pavimento", "calidad.calidades", { materialScope: "pavement", columns: petreosPavimentoSubbalastoColumns }),
  configured("Pétreos", "Filtro", "petreos_filtro", "calidad.calidades", { materialScope: "filter", columns: petreosFiltroColumns }),
  configured("Pétreos", "Subbalasto", "petreos_subbalasto", "calidad.calidades", { materialScope: "subballast", columns: petreosPavimentoSubbalastoColumns }),
  configured("Pedraplén", undefined, "pedraplen", "calidad.pedraplen", { columns: [
    { key: "folio", label: "Folio" }, { key: "fecha_prueba", label: "Fecha" }, { key: "frente", label: "Frente" }, { key: "subfrente", label: "Subfrente" },
    { key: "procedencia_n_comun", label: "Procedencia" }, { key: "tipo_capa", label: "Tipo de capa" }, { key: "entidad", label: "Entidad" }, { key: "dictamen", label: "Dictamen" },
  ] }),
  configured("Suelos", undefined, "suelos", "calidad.calidades", { materialScope: "soils", columns: soilColumns }),
  pending("Vías", "Desgaste", "vias_desgaste"),
  pending("Vías", "Durmientes de concreto", "vias_durmientes_concreto"),
  pending("Vías", "Durmientes de madera", "vias_durmientes_madera"),
  pending("Vías", "Escantillón", "vias_escantillon"),
  pending("Vías", "Soldadura", "vias_soldadura"),
];

export const resultCategories = [...new Set(resultTypes.map(({ category }) => category))];
